import {
  ADMIN_IMAGE_ANALYSIS_API,
  COMMENTARY_REVIEW_API,
  COST_TO_CURE_IMAGE_ANALYSIS_API,
  ImageAnalysisResult,
  REQUEST_ID_HEADER,
  UADAnalysisResult,
  randomUUID,
} from '@aloft/toolkit-shared';
import { Block, Button, Flex, ModeSwitcher, SimpleSelect, Typography } from '@aloft/toolkit-sui';
import { useAuth0 } from '@auth0/auth0-react';

import { useCallback, useState } from 'react';

import { LoadingScreenSamFull } from '../../../components/LoadingScreen/LoadingScreenSamFull.tsx';
import { getBaseUrl } from '../../../hooks/api/getBaseUrl.ts';
import { BulkAnalysisContent } from './BulkAnalysisContent.tsx';
import { CommentaryReviewResults } from './components/CommentaryReviewResults.tsx';
import { CostToCureResults } from './components/CostToCureResults.tsx';
import { CraftsmanResults } from './components/CraftsmanResults.tsx';
import { ImageAnalysisForm, ImageAnalysisFormSubmit } from './components/ImageAnalysisForm.tsx';
import { AnalysisMode } from './components/imageAnalysisFormConfig.ts';
import { ImageCarousel } from './components/ImageCarousel.tsx';
import { RawTextAccordion } from './components/RawTextAccordion.tsx';
import { TimingBar } from './components/TimingBar.tsx';
import { UADResults } from './components/UADResults.tsx';

type SubTab = 'single' | 'bulk';

const SUB_TAB_ITEMS: { id: SubTab; label: string }[] = [
  { id: 'single', label: 'Single' },
  { id: 'bulk', label: 'Bulk' },
];

type MutationState =
  | { status: 'idle' }
  | { status: 'pending' }
  | {
      status: 'success';
      data:
        | (typeof ADMIN_IMAGE_ANALYSIS_API.POST.RESPONSE)['_type']
        | (typeof COMMENTARY_REVIEW_API.POST.RESPONSE)['_type']
        | (typeof COST_TO_CURE_IMAGE_ANALYSIS_API.POST.RESPONSE)['_type'];
    }
  | { status: 'error'; message: string };

export const ImageAnalysisContent = () => {
  const [subTab, setSubTab] = useState<SubTab>('single');
  const [submittedLabel, setSubmittedLabel] = useState('');
  const [analysisMode, setAnalysisMode] = useState<AnalysisMode>('uadRatings');
  const [state, setState] = useState<MutationState>({ status: 'idle' });
  const { getAccessTokenSilently } = useAuth0();

  const handleModeChange = (newMode: AnalysisMode) => {
    setAnalysisMode(newMode);
    if (newMode !== 'uadRatings') setSubTab('single');
  };

  const runAnalysis = useCallback(
    async (
      body:
        | (typeof ADMIN_IMAGE_ANALYSIS_API.POST.REQUEST.BODY)['_type']
        | (typeof COMMENTARY_REVIEW_API.POST.REQUEST.BODY)['_type']
        | (typeof COST_TO_CURE_IMAGE_ANALYSIS_API.POST.REQUEST.BODY)['_type'],
      endpoint:
        | '/api/v2/admin/imageAnalysis'
        | '/api/v2/aiPrompts/commentaryReview'
        | '/api/v2/imageAnalysis/costToCure',
    ) => {
      setState({ status: 'pending' });
      try {
        const token = await getAccessTokenSilently();
        const response = await window.fetch(`${getBaseUrl()}${endpoint}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            [REQUEST_ID_HEADER]: randomUUID(),
          },
          body: JSON.stringify(body),
        });

        const text = await response.text();
        const trimmed = text.trimStart();

        let parsed: unknown;
        try {
          parsed = JSON.parse(trimmed);
        } catch {
          setState({ status: 'error', message: 'Failed to parse server response' });
          return;
        }

        if (!response.ok || (parsed as any)?.error != null) {
          setState({
            status: 'error',
            message: (parsed as any)?.error ?? `Server error ${response.status}`,
          });
          return;
        }

        const result =
          endpoint === '/api/v2/imageAnalysis/costToCure'
            ? COST_TO_CURE_IMAGE_ANALYSIS_API.POST.RESPONSE.parse(parsed)
            : endpoint === '/api/v2/aiPrompts/commentaryReview'
              ? COMMENTARY_REVIEW_API.POST.RESPONSE.parse(parsed)
            : ADMIN_IMAGE_ANALYSIS_API.POST.RESPONSE.parse(parsed);
        setState({ status: 'success', data: result });
      } catch (err) {
        setState({
          status: 'error',
          message: err instanceof Error ? err.message : 'Unknown error',
        });
      }
    },
    [getAccessTokenSilently],
  );

  const handleSubmit = (values: ImageAnalysisFormSubmit) => {
    setSubmittedLabel(values.submittedLabel);
    const common = {
      model: values.model,
      promptOverride: values.promptOverride,
    };

    if (values.analysisMode === 'costToCure') {
      if (values.inputMode === 'toolkitOrder') {
        void runAnalysis(
          {
            ...common,
            mode: 'toolkitOrder',
            orderID: values.orderID,
            compress: values.compress,
            sample: values.sample,
          },
          '/api/v2/imageAnalysis/costToCure',
        );
      } else {
        void runAnalysis(
          {
            ...common,
            mode: 'manual',
            reportId: values.reportId,
            appraisal: values.appraisal,
            compress: values.compress,
            sample: values.sample,
          },
          '/api/v2/imageAnalysis/costToCure',
        );
      }
      return;
    }

    if (values.analysisMode === 'commentaryReview') {
      void runAnalysis(
        {
          orderID: values.orderID,
          model: values.model,
          promptOverride: values.promptOverride,
          debug: true,
        },
        '/api/v2/aiPrompts/commentaryReview',
      );
      return;
    }

    const adminCommon = {
      ...common,
      mode: values.analysisMode,
      compress: values.compress,
      sample: values.sample,
    };
    if (values.lookupType === 'mlsId') {
      void runAnalysis(
        { ...adminCommon, lookupType: 'mlsId', mlsid: values.mlsid, listingID: values.listingID },
        '/api/v2/admin/imageAnalysis',
      );
    } else {
      void runAnalysis(
        { ...adminCommon, lookupType: 'address', address: values.address },
        '/api/v2/admin/imageAnalysis',
      );
    }
  };

  const isPending = state.status === 'pending';
  const data = state.status === 'success' ? state.data : null;
  const errorMessage = state.status === 'error' ? state.message : null;
  const reset = () => setState({ status: 'idle' });

  const showResults = data != null || errorMessage != null;
  const timing = data?.mode !== 'commentaryReview' ? data?._debug?.timing : undefined;
  const tokenUsage = data?._debug?.tokenUsage;
  const imageURLs = data?.mode !== 'commentaryReview' ? (data?._debug?.imageURLs ?? []) : [];

  return (
    <Flex flexDirection="column" gap="20px">
      {/* Mode selector — always visible at the top */}
      <Flex gap="16px" alignItems="flex-end">
        <SimpleSelect
          label="Mode"
          value={analysisMode}
          onChange={(e) => handleModeChange(e.target.value as AnalysisMode)}
          width="200px"
          size="sm"
        >
          <option value="uadRatings">UAD Ratings</option>
          <option value="craftsmanQuality">Craftsman Quality</option>
          <option value="costToCure">Cost-to-Cure</option>
          <option value="commentaryReview">Commentary Review</option>
        </SimpleSelect>
      </Flex>

      {/* Single/Bulk toggle — only for UAD Ratings */}
      {analysisMode === 'uadRatings' && (
        <ModeSwitcher
          items={SUB_TAB_ITEMS}
          value={subTab}
          variant="secondary"
          size="sm"
          onChange={(v) => setSubTab(v as SubTab)}
        />
      )}

      {/* Single analysis tab */}
      <div style={{ display: subTab === 'single' ? undefined : 'none' }}>
        {/* Always mounted to preserve form state */}
        <div style={{ display: showResults || isPending ? 'none' : undefined }}>
          <ImageAnalysisForm onSubmit={handleSubmit} isSubmitting={isPending} analysisMode={analysisMode} />
        </div>

        {isPending && (
          <Block position="relative" minHeight="400px">
            <LoadingScreenSamFull
              isLoading
              what="AI Prompt"
              wrapperProps={{ position: 'absolute', zIndex: '10' }}
            />
          </Block>
        )}

        {showResults && (
          <Flex flexDirection="column" gap="20px">
            <Flex justifyContent="space-between" alignItems="center">
              {submittedLabel !== '' && <Typography variant="h2">{submittedLabel}</Typography>}
              <Button text="New Analysis" onClick={() => reset()} size="sm" />
            </Flex>

            {errorMessage != null && (
              <Block padding="12px" borderRadius="4px" backgroundColor="alertErrorLight">
                <Typography variant="body1">{errorMessage}</Typography>
              </Block>
            )}

            {timing != null && <TimingBar timing={timing} tokenUsage={tokenUsage} />}

            {data?._debug?.promptBlockReason != null && (
              <Block padding="10px 16px" borderRadius="4px" backgroundColor="alertWarningLight">
                <Typography variant="body2">
                  Prompt blocked by Gemini — reason: <strong>{data._debug.promptBlockReason}</strong>
                </Typography>
              </Block>
            )}

            {data != null && analysisMode === 'craftsmanQuality' && (
              <CraftsmanResults data={data as ImageAnalysisResult} />
            )}
            {data != null && analysisMode === 'uadRatings' && (
              <UADResults data={data as UADAnalysisResult} />
            )}
            {data != null && analysisMode === 'costToCure' && (
              <CostToCureResults data={data as (typeof COST_TO_CURE_IMAGE_ANALYSIS_API.POST.RESPONSE)['_type']} />
            )}
            {data != null && analysisMode === 'commentaryReview' && (
              <CommentaryReviewResults data={data as (typeof COMMENTARY_REVIEW_API.POST.RESPONSE)['_type']} />
            )}

            {analysisMode !== 'costToCure' && imageURLs.length > 0 && <ImageCarousel urls={imageURLs} />}

            {data?._debug?.rawText != null && (
              <RawTextAccordion
                rawText={data._debug.rawText}
                thoughtsText={data._debug.thoughtsText}
              />
            )}
          </Flex>
        )}
      </div>

      {/* Bulk analysis tab — always mounted to preserve state */}
      <div style={{ display: subTab === 'bulk' ? undefined : 'none' }}>
        <BulkAnalysisContent analysisMode={analysisMode} />
      </div>
    </Flex>
  );
};
